import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateReviewInput, ReviewListQuery } from '@teentack/shared';
import { notificationKeys } from '@/features/notifications/use-notifications';
import {
  createReview,
  fetchPendingReviews,
  fetchReputation,
  fetchReputations,
  fetchReviews,
} from './reviews.api';

export const reviewKeys = {
  all: ['reviews'] as const,
  list: (query: Partial<ReviewListQuery>) => ['reviews', 'list', query] as const,
  pending: ['reviews', 'pending'] as const,
  reputation: (personId: string) => ['reviews', 'reputation', personId] as const,
  reputations: (personIds: string[]) => ['reviews', 'reputations', [...personIds].sort()] as const,
};

export function useReviews(query: Partial<ReviewListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: reviewKeys.list(query),
    queryFn: () => fetchReviews(query),
    enabled,
  });
}

/** Approved work the signed-in account still owes a review on. */
export function usePendingReviews(enabled = true) {
  return useQuery({
    queryKey: reviewKeys.pending,
    queryFn: fetchPendingReviews,
    enabled,
    staleTime: 30_000,
  });
}

export function useReputation(personId: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.reputation(personId ?? ''),
    queryFn: () => fetchReputation(personId as string),
    enabled: Boolean(personId),
    staleTime: 60_000,
  });
}

/**
 * Scores for a whole list in one request.
 *
 * The key is sorted, so the same set of people in a different order reuses the
 * cached answer instead of refetching.
 */
export function useReputations(personIds: string[], enabled = true) {
  return useQuery({
    queryKey: reviewKeys.reputations(personIds),
    queryFn: () => fetchReputations(personIds),
    enabled: enabled && personIds.length > 0,
    staleTime: 60_000,
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReviewInput) => createReview(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
